/**
 * APPOINTMENT REVISIONS: which of two projections is newer, for ONE appointment.
 *
 * `booking-v1@1.4.0` adds `Appointment.revision: Int!` and
 * `AppointmentAccessView.revision: Int!`. The contract's rule, for one
 * appointment:
 *
 *   incoming >  held   the incoming projection is newer; replace what you hold
 *   incoming == held   you already hold this state
 *   incoming <  held   the incoming projection is older; keep what you hold
 *
 * The failure this closes is real and silent: a slow response from an earlier
 * request arriving after a faster later one, and overwriting current truth with
 * a stale projection. A client then shows an appointment as CONFIRMED after it
 * was cancelled, with no error anywhere.
 *
 * COMPARING ACROSS APPOINTMENTS IS MEANINGLESS, and the contract says so —
 * every appointment's counter starts at 1 and moves independently. A global
 * "highest revision wins" is therefore not a stricter version of this rule but
 * a different and wrong one: it would discard a legitimate update to
 * appointment B because appointment A happens to have been edited more often.
 * `decideRevision` refuses a cross-appointment comparison rather than guessing.
 */

/** Why an incoming projection was or was not applied. */
export type RevisionDecision =
  /** Strictly newer for the same appointment. Replace what is held. */
  | { kind: "apply"; revision: number }
  /** The same projection we already hold. Not an error; simply nothing to do. */
  | { kind: "duplicate"; revision: number }
  /** Older for the same appointment. Keep what is held. */
  | { kind: "stale"; held: number; incoming: number }
  /** Nothing is held yet, so there is nothing to be newer than. */
  | { kind: "first"; revision: number }
  /**
   * Fail closed. The revision is not a positive integer, or the two
   * projections describe DIFFERENT appointments. Neither is a value judgement
   * about which is newer — both mean the question cannot be answered.
   */
  | { kind: "unusable"; reason: "invalid-revision" | "different-appointment" };

/**
 * A positive integer, and nothing looser.
 *
 * `Int!` is non-null on the wire, so absence is a contract violation rather
 * than a state. Zero and negatives are refused because the contract starts
 * counters at 1 — a zero means a server that did not populate the field, or a
 * client that defaulted it, and defaulting a revision to 0 would make every
 * real projection look newer than it. Non-integers are refused because a
 * float cannot be a counter and comparing one hides a type error.
 *
 * `typeof value === "number"` excludes a numeric STRING deliberately: `"5" > 4`
 * is true in JavaScript and `"10" < "9"` is also true, so admitting strings
 * would give an ordering that works until it silently does not.
 */
export function isValidRevision(value: unknown): value is number {
  return typeof value === "number" && Number.isInteger(value) && value > 0;
}

/** One appointment's known projection. */
export interface HeldRevision {
  appointmentId: string;
  revision: number;
}

/**
 * Decide whether an incoming projection may replace what is held.
 *
 * Both identifiers are required. A caller that does not know which appointment
 * it holds cannot ask this question, and answering it anyway is the
 * cross-appointment comparison the contract forbids.
 */
export function decideRevision(
  held: HeldRevision | null,
  incoming: { appointmentId: unknown; revision: unknown }
): RevisionDecision {
  if (typeof incoming.appointmentId !== "string" || incoming.appointmentId.length === 0) {
    return { kind: "unusable", reason: "different-appointment" };
  }
  if (!isValidRevision(incoming.revision)) {
    return { kind: "unusable", reason: "invalid-revision" };
  }
  if (held === null) return { kind: "first", revision: incoming.revision };
  if (held.appointmentId !== incoming.appointmentId) {
    return { kind: "unusable", reason: "different-appointment" };
  }
  if (!isValidRevision(held.revision)) return { kind: "unusable", reason: "invalid-revision" };

  if (incoming.revision > held.revision) return { kind: "apply", revision: incoming.revision };
  if (incoming.revision === held.revision) return { kind: "duplicate", revision: incoming.revision };
  return { kind: "stale", held: held.revision, incoming: incoming.revision };
}

/** Does this decision mean the caller should adopt the incoming projection? */
export function shouldApply(decision: RevisionDecision): boolean {
  return decision.kind === "apply" || decision.kind === "first";
}
